import React, { useMemo } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import BasicTable from '@/components/Table/BasicTable';
import FileBasicTableRow from '@/components/Table/FileBasicTableRow';
import BasicTableRowLoadMoreButton from '@/components/Table/BasicTableRowLoadMoreButton';
import useResponsiveness from '@/hooks/useResponsiveness';
import Tooltip from '@/components/Tooltip';
import { getSelectableFileId } from '@/constants/utils/file_selection';

const createStyles = (colors) => StyleSheet.create({
  checkboxIcon: {
    width: 16,
    height: 16,
    borderRadius: 4,
    borderWidth: 1,
    backgroundColor: colors.solid_color_primary,
    borderColor: colors.text_color_secondary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxIconSelected: {
    backgroundColor: colors.text_color_secondary,
  },
});

const FilesTable = ({
  files = [],
  isLoading = false,
  pagination = {},
  selectedFiles,
  onFileClick,
  onFileSelect,
  onSelectAll,
  onLoadMore,
  excludeSize = false,
  excludeStatus = false,
  showDelete = false,
  onDelete,
  maxSelection = 10,
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { isXS } = useResponsiveness();
  const selectableIds = useMemo(() => {
    const ids = [];
    for (const file of files) {
      const fileId = getSelectableFileId(file);
      if (fileId) ids.push(fileId);
    }
    return ids;
  }, [files]);
  const allSelected = selectableIds.length > 0
    && selectableIds.every((fileId) => selectedFiles.has(fileId));
  const someSelected = selectedFiles.size > 0;
  const atSelectionLimit = Number.isFinite(maxSelection) && selectedFiles.size >= maxSelection;

  const tableColumns = useMemo(() => {
    const columns = [
      {
        id: 'select',
        width: isXS ? 40 : 52,
        header: (
          <Tooltip label={allSelected ? 'Deselect all' : 'Select all'}>
            <Pressable 
              style={{ width: 28, marginRight: isXS ? 5 : 12, justifyContent: 'center', alignItems: 'center' }}
              onPress={onSelectAll}
              accessibilityRole="checkbox"
              accessibilityLabel={allSelected ? 'Deselect all' : 'Select all'}
              accessibilityState={{ checked: allSelected ? true : (someSelected ? 'mixed' : false) }}
            >
              <View style={[
                styles.checkboxIcon,
                someSelected && styles.checkboxIconSelected
              ]}>
                {someSelected && (
                  <Icon name={allSelected ? 'check' : 'minus'} size={10} color={colors.solid_color_primary} />
                )}
              </View>
            </Pressable>
          </Tooltip>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        width: 'auto',
        headerStyle: { flex: 1, minWidth: 0 },
        cellStyle: { flex: 1, minWidth: 0 },
      },
    ];

    const rightAlignedType = excludeSize && excludeStatus;

    if (!isXS) {
      columns.push({
        id: 'type',
        header: 'Type',
        width: 100,
        headerStyle: { paddingHorizontal: 8, alignItems: rightAlignedType ? 'flex-end' : 'flex-start' },
        cellStyle: { paddingHorizontal: 8, alignItems: rightAlignedType ? 'flex-end' : 'flex-start' },
      });
    }

    if (!excludeSize && !isXS) {
      columns.push({
        id: 'size',
        header: 'Size',
        width: 80,
        headerStyle: { paddingHorizontal: 8, alignItems: 'flex-end' },
        cellStyle: { paddingHorizontal: 8, alignItems: 'flex-end' },
      });
    }

    if (!excludeStatus && !isXS) {
      columns.push({
        id: onDelete ? 'delete' : 'status',
        header: 'Status',
        width: 90,
        headerStyle: { paddingLeft: 8, alignItems: 'center' },
        cellStyle: { paddingLeft: 8, alignItems: 'center' },
      });
    }

    if (showDelete && onDelete) {
      columns.push({
        id: 'delete',
        header: isXS ? '' :'Delete',
        width: isXS ? 26 : 90,
        headerStyle: { paddingLeft: 8, alignItems: isXS ? 'flex-end' : 'center' },
        cellStyle: { paddingLeft: 8, alignItems: isXS ? 'flex-end' : 'center' },
      });
    }

    return columns;
  }, [allSelected, colors, excludeSize, isXS, excludeStatus, onDelete, showDelete, onSelectAll, someSelected, styles.checkboxIcon, styles.checkboxIconSelected]);

  return (
    <BasicTable columns={tableColumns}>
      {files.map((file) => (
        <FileBasicTableRow
          key={getSelectableFileId(file) || file.id}
          file={file}
          onPress={onFileClick}
          isSelected={selectedFiles.has(getSelectableFileId(file))}
          onSelect={onFileSelect}
          selectionDisabled={atSelectionLimit && !selectedFiles.has(getSelectableFileId(file))}
          isXS={isXS}
          excludeStatus={excludeStatus}
          showDelete={showDelete}
          onDelete={onDelete}
        />
      ))}
      {pagination.hasMore && (
        <BasicTableRowLoadMoreButton
          onPress={onLoadMore}
          isLoading={isLoading}
        />
      )}
    </BasicTable>
  );
};

export default React.memo(FilesTable);
